import { TelephoneCountryCodesEnum } from '../../../../resources/base/models';
import { formatPhoneNumber, removeLeadingZerosFromMobilePhone } from '../../../../services/common/format';
import { DeepRequired } from '../../../../services/common/miscUtils.types';
import { uiDateToApiDate } from '../../utils/formatDate';
import { getEmploymentsForRequestMapper, getMainEmployment } from '../utils';
import {
  AddressDataFormType,
  CompanyDataFormType,
  ContactDataFormType,
  FinancialDataFormType,
  PaymentDataFormType,
} from './initialValues';

export const contactDataFormRequestMapper = (
  values: ContactDataFormType,
  currentCreditApplicationData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse
): CreditIntegration.Schemas.UpdateIndividualCustomerDataRequest => {
  const milesEmailId = currentCreditApplicationData.contactData?.emails?.[0].milesEmailId;
  const milesMobilePhoneNumberId = currentCreditApplicationData.contactData?.milesMobilePhoneNumberId;
  const milesTelephoneNumberId = currentCreditApplicationData.contactData?.milesTelephoneNumberId;

  const mainEmployment = getMainEmployment(currentCreditApplicationData);

  return {
    customerType: 'INDIVIDUAL',
    gender: values.gender,
    firstName: values.firstName,
    lastName: values.lastName,
    birthName: values.birthName,
    dateOfBirth: uiDateToApiDate(values.dateOfBirth),
    employments: values.profession
      ? [
          {
            milesemploymentId: mainEmployment?.milesemploymentId,
            posFrOccupation: values.profession,
            milesInvolvedInCompanyCategory: values._employmentCategory || 'MAIN_EMPLOYMENT',
          },
        ]
      : [],
    contactData: {
      emails: values.email ? [{ emailAddress: values.email, milesEmailId }] : [],
      mobilePhoneCountryCode: values.mobilePhoneCountryCode,
      mobilePhoneNumber: removeLeadingZerosFromMobilePhone(values.mobilePhone),
      milesMobilePhoneNumberId,
      telephoneCountryCode: values.telephonePhone ? TelephoneCountryCodesEnum['+33'] : undefined,
      telephoneNumber: formatPhoneNumber(values.telephonePhone),
      milesTelephoneNumberId,
    },
    countryOfBirth: values.birthCountry,
    placeOfBirthZipCode: values.zipCodeOfBirth,
    placeOfBirth: values.placeOfBirth,
    nationality: values.nationality,
    financialDisclosure: {
      housingSituation: values.residenceStatus,
    },
  };
};

export const addressDataFormRequestMapper = (
  values: AddressDataFormType
): CreditIntegration.Schemas.UpdateIndividualCustomerDataRequest => {
  return {
    customerType: 'INDIVIDUAL',
    addresses: [
      {
        addressLine1: values.addressLine1,
        optionalLine: values.optionalLine,
        zipCode: values.zipCode,
        city: values.city,
        countryCode: values.countryCode,
      },
    ],
  };
};

export const financialDataFormRequestMapper = (
  values: DeepRequired<FinancialDataFormType>,
  currentCreditApplicationData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse,
  isOccupationGroupA: boolean,
  isOccupationGroupB: boolean
): CreditIntegration.Schemas.UpdateIndividualCustomerDataRequest => {
  const mainEmployment = getMainEmployment(currentCreditApplicationData);

  return {
    customerType: 'INDIVIDUAL',
    maritalStatus: values.maritalStatus,
    numberOfDependentChildren: values.numberOfDependentChildren,
    employments: [
      {
        milesemploymentId: mainEmployment?.milesemploymentId,
        contractType: values.contractType,
        ...(!isOccupationGroupB && {
          period: { startDate: uiDateToApiDate(values.employmentStartDate) },
        }),
        ...(!isOccupationGroupA && {
          employer: {
            name: values.employer,
            addresses: [
              {
                countryCode: values.countryCodeEmployer,
                zipCode: values.zipCodeEmployer,
                city: values.cityEmployer,
              },
            ],
          },
        }),
      },
    ],
    financialDisclosure: {
      incomes: [
        {
          incomeType: 'PERSONAL_INCOME',
          amount: { netAmount: values.netIncomeAmount },
        },
        {
          incomeType: 'ADDITIONAL',
          amount: { netAmount: values.additionalIncomeAmount },
        },
      ],
      expenses: [
        {
          personal: [
            {
              expenseType: 'RENT',
              amount: { netAmount: values.monthlyRent },
            },
            {
              expenseType: 'ADDITIONAL',
              amount: { netAmount: values.otherMonthlyCosts },
            },
          ],
        },
      ],
    },
  };
};

export const paymentDataFormRequestMapper = (
  values: PaymentDataFormType
): CustomerIntegration.Schemas.PostAddBankAccountRequest => {
  return {
    customerData: {
      paymentData: {
        iban: values.iban!,
        milesAccountHolder: values.bankAccountOwner,
        milesBankAccountOpeningDate: uiDateToApiDate(values.openingDate)!,
        bic: values.bic,
      },
    },
  };
};

export const companyDataFormRequestMapper = (
  values: CompanyDataFormType,
  currentCreditApplicationData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse
): CreditIntegration.Schemas.UpdateIndividualCustomerDataRequest => {
  return {
    customerType: 'INDIVIDUAL',
    employments: getEmploymentsForRequestMapper(values, currentCreditApplicationData),
  };
};
